import Axios from 'axios';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import debounce from 'lodash/debounce';
import type { UseTranslationOptions } from 'react-i18next';
import { initReactI18next, useTranslation } from 'react-i18next';

import { getAppEnv, getAuthApiUrl } from './app-config';
import { logError } from './helpers/log';

const appName = 'pp-gateway' as const;
export type Namespace = typeof appName | 'languages';

interface AuthApiLanguage {
  code: string;
  fallback_code?: string;
  name: string;
}

const EnglishLanguage: AuthApiLanguage = { code: 'en', name: 'English' };

const i18nextReloadResources = debounce(() => i18n.reloadResources(), 5000);

export function changeLanguage(lng: string) {
  if (i18n.language === lng) {
    // no-op
    return;
  }
  return i18n.changeLanguage(lng);
}

function initI18N(languages: AuthApiLanguage[]) {
  i18n
    // load translation using http -> see /public/locales (i.e. https://github.com/i18next/react-i18next/tree/master/example/react/public/locales)
    // learn more: https://github.com/i18next/i18next-http-backend
    // want your translations to be loaded from a professional CDN? => https://github.com/locize/react-tutorial#step-2---use-the-locize-cdn
    .use(Backend)
    // detect user language
    // learn more: https://github.com/i18next/i18next-browser-languageDetector
    .use(LanguageDetector)
    // pass the i18n instance to react-i18next.
    .use(initReactI18next)
    // init i18next
    // for all options read: https://www.i18next.com/overview/configuration-options
    .init({
      backend: {
        addPath: `${getAuthApiUrl()}/api/v1/translations/add/{{ns}}/{{lng}}`,
        crossDomain: true,
        customHeaders: {
          'X-App-Name': appName,
        },
        loadPath: `${getAuthApiUrl()}/api/v1/translations/load/{{ns}}/{{lng}}`,
        parsePayload: (namespace: string, key: string, fallbackValue: string) => {
          i18nextReloadResources();
          // log.info(`missing namespace=${namespace} key=${key} fallbackValue=${fallbackValue}`);
          return { [key]: fallbackValue || '' };
        },
        withCredentials: true,
      },
      debug: getAppEnv() === 'development',
      fallbackLng: (code: string | undefined) => {
        const language = languages.find((l) => l.code === code);
        if (!language) {
          // default to the first language returned by auth-api
          return languages[0].fallback_code ?? languages[0].code;
        }
        return language.fallback_code ?? [];
      },
      interpolation: {
        escapeValue: false, // not needed for react as it escapes by default
      },
      lowerCaseLng: true, // we store them like this in the db
      ns: appName,
      saveMissing: true,
      saveMissingTo: 'fallback',
      supportedLngs: languages.map((l) => l.code),
    });
}

const domain = window.location.hostname.replace(/account/, 'my');
Axios.get<AuthApiLanguage[]>(
  `${getAuthApiUrl()}/api/v1/translations/languages/supported?domain=${encodeURIComponent(domain)}`
)
  .then((response) => response.data)
  .catch((error) => {
    logError(error);
    return [] as AuthApiLanguage[];
  })
  .then((supportedLngs) => {
    if (supportedLngs.length === 0) {
      supportedLngs.push(EnglishLanguage);
    }
    initI18N(supportedLngs);
  });

export default i18n;

export function useAppTranslation(ns?: Namespace, options?: UseTranslationOptions<Namespace>) {
  return useTranslation(ns, options);
}
